import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
  SecurityContext,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { LUCIDE_ICONS } from '@compsych-ui-components/shared/assets/lucide-icons.generated';
import { ButtonComponent } from '../button/button';
import { AccordionButton, AccordionItem, AccordionVariant, NestedAccordionItem } from './accordion-types';

export type { AccordionButton, AccordionItem, AccordionVariant, NestedAccordionItem };

let nextId = 0;

// AccordionItemComponent is defined before AccordionComponent because AccordionComponent
// uses it directly in its imports. AccordionItemComponent references AccordionComponent
// via forwardRef to break the forward-reference cycle within this file.
@Component({
  selector: 'compsych-accordion-item',
  standalone: true,
  imports: [ButtonComponent, forwardRef(() => AccordionComponent)],
  template: `
    <button
      class="trigger"
      type="button"
      [id]="triggerId"
      [attr.aria-expanded]="expanded"
      [attr.aria-controls]="panelId"
      (click)="toggle()"
    >
      @if (iconSvg) {
        <span class="trigger-leading-icon" [innerHTML]="iconSvg" aria-hidden="true"></span>
      }
      <span class="trigger-label">{{ title }}</span>
      <span class="trigger-toggle material-symbols-rounded" aria-hidden="true">{{ expanded ? 'remove' : 'add' }}</span>
    </button>
    <div
      [id]="panelId"
      role="region"
      [attr.aria-labelledby]="triggerId"
      [hidden]="!expanded"
    >
      @if (content) {
        <div class="content" [innerHTML]="sanitizedContent"></div>
      }
      @if (nestedContent.length) {
        <compsych-accordion [items]="nestedContent" variant="level-2" [nested]="true" />
      }
      @if (buttons.length) {
        <div class="actions">
          @for (btn of buttons; track btn.label) {
            <compsych-button [label]="btn.label" variant="filled" size="small" (click)="btn.action()" />
          }
        </div>
      }
    </div>
  `,
  styleUrl: './accordion-item.css',
  host: {
    class: 'compsych-accordion-item',
    '[class.level-2]': 'variant === "level-2"',
    '[class.text-only]': 'variant === "text-only"',
    '[class.nested]': 'nested',
  },
})
/**
 * A single expandable row used internally by `AccordionComponent`.
 * Consumers should use `compsych-accordion` with an `items` array rather
 * than placing `compsych-accordion-item` elements directly.
 */
export class AccordionItemComponent {
  readonly triggerId = `accordion-trigger-${nextId}`;
  readonly panelId = `accordion-panel-${nextId++}`;

  /** Text shown in the trigger button header. */
  @Input() title = '';
  /** HTML string rendered in the panel body when expanded. Sanitized before display. */
  @Input() content = '';
  /** Action buttons rendered at the bottom of the panel. */
  @Input() buttons: AccordionButton[] = [];
  /** Items rendered as a nested level-2 accordion inside this panel. */
  @Input() nestedContent: NestedAccordionItem[] = [];
  /** Visual style — inherited from the parent AccordionComponent. */
  @Input() variant: AccordionVariant = 'default';
  /** True when this item is rendered inside a parent accordion panel. Adds nested styling. Set automatically by AccordionComponent — do not set directly. */
  @Input() nested = false;

  /** Lucide icon name (kebab-case) shown to the left of the title. Pass an empty string to show no icon. */
  @Input()
  set icon(name: string) {
    const svg = LUCIDE_ICONS[name];
    this.iconSvg = svg ? this.sanitizer.bypassSecurityTrustHtml(svg) : null;
  }

  iconSvg: SafeHtml | null = null;

  get sanitizedContent(): SafeHtml {
    return this.sanitizer.sanitize(SecurityContext.HTML, this.content) ?? '';
  }

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly el: ElementRef<HTMLElement>,
  ) {}

  /** Whether this panel is currently open. Supports boolean coercion (e.g. `[expanded]="true"`). */
  @Input()
  get expanded(): boolean { return this._expanded; }
  set expanded(value: BooleanInput) { this._expanded = coerceBooleanProperty(value); }
  private _expanded = false;

  /** Emitted when the panel is toggled. `true` = opening, `false` = closing. */
  @Output() expandedChange = new EventEmitter<boolean>();

  toggle(): void {
    this._expanded = !this._expanded;
    this.expandedChange.emit(this._expanded);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (!(target instanceof HTMLButtonElement) || !target.classList.contains('trigger')) return;

    const parent = this.el.nativeElement.parentElement;
    if (!parent) return;

    const triggers = Array.from(
      parent.querySelectorAll<HTMLButtonElement>(':scope > compsych-accordion-item > button.trigger'),
    );
    const currentIndex = triggers.indexOf(target);
    let focusIndex = -1;

    switch (event.key) {
      case 'ArrowDown': focusIndex = currentIndex + 1; break;
      case 'ArrowUp':   focusIndex = currentIndex - 1; break;
      case 'Home':      focusIndex = 0; break;
      case 'End':       focusIndex = triggers.length - 1; break;
      default: return;
    }

    if (focusIndex >= 0 && focusIndex < triggers.length) {
      triggers[focusIndex].focus();
      event.preventDefault();
    }
  }
}

@Component({
  selector: 'compsych-accordion',
  standalone: true,
  imports: [AccordionItemComponent],
  template: `
    @for (item of items; track $index; let i = $index) {
      <compsych-accordion-item
        [title]="item.title"
        [content]="item.content ?? ''"
        [icon]="item.icon ?? ''"
        [buttons]="item.buttons ?? []"
        [nestedContent]="item.nestedContent ?? []"
        [variant]="variant"
        [nested]="nested"
        [expanded]="expandedSet.has(i)"
        (expandedChange)="onExpandedChange(i, $event)"
      />
    }
  `,
  styleUrl: './accordion.css',
  host: {
    '[class.level-2]': 'variant === "level-2"',
    '[class.text-only]': 'variant === "text-only"',
    '[class.nested]': 'nested',
  },
})
export class AccordionComponent {
  /** Items to render as expandable rows. */
  @Input() items: AccordionItem[] = [];
  /** Visual style applied to all items. `'default'` is the standard card style; `'level-2'` is compact nested style; `'text-only'` removes card chrome. */
  @Input() variant: AccordionVariant = 'default';
  /** Set automatically when this accordion is rendered inside another accordion panel. Do not set directly. */
  @Input() nested = false;
  /** When true, multiple panels can be open simultaneously. Defaults to false — opening one panel closes any others. Supports boolean coercion. */
  @Input()
  get multi(): boolean { return this._multi; }
  set multi(value: BooleanInput) { this._multi = coerceBooleanProperty(value); }
  private _multi = false;

  expandedSet = new Set<number>();

  onExpandedChange(index: number, isExpanded: boolean): void {
    if (isExpanded) {
      if (!this._multi) this.expandedSet.clear();
      this.expandedSet.add(index);
    } else {
      this.expandedSet.delete(index);
    }
    this.expandedSet = new Set(this.expandedSet);
  }
}
